import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import type { AssortmentFilterValueResponse } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface JcAssortmentFilterValuesState {
  byProduct: Record<string, string[]>
}

export const useJcAssortmentFilterValuesStore = defineStore({
  id: "jc.assortment.filter.values",
  state: (): JcAssortmentFilterValuesState => ({
    byProduct: {},
  }),
  actions: {
    async fetch(assortmentProductId: string): Promise<string[]> {
      if (!assortmentProductId) return []
      try {
        const url = CONSTANT.API.JC_FILTER.ASSORTMENT_FILTER_VALUE.LIST.replace("{id}", assortmentProductId)
        const result = (await httpService.get(url)) as { data: AssortmentFilterValueResponse }
        const ids = result?.data?.filterOptionIds ?? []
        this.byProduct[assortmentProductId] = ids
        return ids
      } catch (error) {
        console.error(`Failed to fetch filter options for assortment product ${assortmentProductId}`, error)
        this.byProduct[assortmentProductId] = []
        return []
      }
    },

    async fetchMany(assortmentProductIds: string[]): Promise<Record<string, string[]>> {
      const ids = assortmentProductIds.filter((id): id is string => !!id)
      const lists = await Promise.all(ids.map((id) => this.fetch(id)))
      const result: Record<string, string[]> = {}
      ids.forEach((id, i) => {
        result[id] = lists[i]
      })
      return result
    },

    async replace(assortmentProductId: string, filterOptionIds: string[]): Promise<boolean> {
      if (!assortmentProductId) return false
      try {
        const url = CONSTANT.API.JC_FILTER.ASSORTMENT_FILTER_VALUE.REPLACE.replace("{id}", assortmentProductId)
        await httpService.put(url, { filterOptionIds })
        this.byProduct[assortmentProductId] = [...filterOptionIds]
        return true
      } catch (error) {
        console.error(`Failed to replace filter options for assortment product ${assortmentProductId}`, error)
        return false
      }
    },

    async replaceMany(updates: Record<string, string[]>, onProgress?: (done: number, total: number, currentId: string, success: boolean) => void): Promise<{ ok: string[]; failed: string[] }> {
      const entries = Object.entries(updates)
      const ok: string[] = []
      const failed: string[] = []
      const total = entries.length
      let done = 0
      // Sequential — keeps the backend from getting hammered and prevents
      // race conditions on the same configurator's M:N table.
      for (const [id, ids] of entries) {
        const success = await this.replace(id, ids)
        done++
        if (success) ok.push(id)
        else failed.push(id)
        try {
          onProgress?.(done, total, id, success)
        } catch (e) {
          console.error("bulk progress callback failed", e)
        }
      }
      return { ok, failed }
    },

    get(assortmentProductId: string): string[] {
      return this.byProduct[assortmentProductId] ?? []
    },
  },
})
